import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useToast } from 'components/ui/toast';
import { useExcludeTechnician, useReinstateTechnician } from 'hooks/useTechnicians';
import { saveErrorMessage } from 'utils/errorMessage';
import { exclusionReasonLabel } from './validation';
import { EXCLUSION_REASONS, type ExclusionReason } from 'api/technicianImportContract';

export type ExclusionDialogMode = 'exclude' | 'edit' | 'reinstate';

interface ExclusionDialogProps {
  /** Null = closed: the dialog stays mounted so Radix can hand the focus back. */
  mode: ExclusionDialogMode | null;
  technicianId: number;
  name: string;
  /** Pre-fills « Modifier le motif » (M15). */
  currentReason: ExclusionReason | null;
  onClose: () => void;
  /** After a confirmed write, once the dialog released the focus: the page moves it, the trigger may be gone. */
  onDone: () => void;
}

const REASON_ID = 'technician-exclusion-reason';

/** Bannir, Modifier le motif and Réintégrer (CA19): one dialog, one request per confirmation. */
const ExclusionDialog = ({ mode: openMode, technicianId, name, currentReason, onClose, onDone }: ExclusionDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const exclude = useExcludeTechnician();
  const reinstate = useReinstateTechnician();
  // The last open mode, so the closing frame keeps its title.
  const [mode, setMode] = useState<ExclusionDialogMode>('exclude');
  const [reason, setReason] = useState<ExclusionReason | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // A fresh form on each opening, set while rendering (no effect); « Modifier le motif » starts from the current reason (M15).
  const [shownFor, setShownFor] = useState<ExclusionDialogMode | null>(null);
  if (openMode !== shownFor) {
    setShownFor(openMode);
    if (openMode) {
      setMode(openMode);
      setReason(openMode === 'edit' ? currentReason : null);
      setSubmitted(false);
    }
  }
  // `isPending` reaches the render a tick late; the ref closes the double-click window.
  const inFlight = useRef(false);
  const succeeded = useRef(false);

  const pending = exclude.isPending || reinstate.isPending;
  const missingReason = submitted && mode !== 'reinstate' && reason === null;
  const unchanged = mode === 'edit' && reason === currentReason;

  const settle = (success: string) => ({
    onSuccess: () => {
      toast.success(success);
      succeeded.current = true;
      onClose();
    },
    onError: (error: Error) => toast.error(saveErrorMessage(t, error)),
    onSettled: () => {
      inFlight.current = false;
    },
  });

  const confirm = () => {
    if (inFlight.current || unchanged) return;
    setSubmitted(true);
    if (mode === 'reinstate') {
      inFlight.current = true;
      reinstate.mutate(technicianId, settle(t('Technician reinstated')));
      return;
    }
    if (reason === null) return;
    inFlight.current = true;
    exclude.mutate({ id: technicianId, reason }, settle(mode === 'edit' ? t('Reason updated') : t('Technician banned')));
  };

  const title = {
    exclude: t('Ban {{name}}?', { name }),
    edit: t('Change the reason for {{name}}', { name }),
    reinstate: t('Reinstate {{name}}?', { name }),
  }[mode];
  const confirmLabel = { exclude: t('Ban'), edit: t('Save changes'), reinstate: t('Reinstate') }[mode];

  return (
    <Dialog open={openMode !== null} onOpenChange={(open) => !open && !pending && onClose()}>
      <DialogContent
        onCloseAutoFocus={(event) => {
          // After a write the trigger may no longer exist: `onDone` places the focus instead.
          if (!succeeded.current) return;
          succeeded.current = false;
          event.preventDefault();
          onDone();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {/* M11 — no count of the ongoing missions: they stay, whatever their number. */}
          <DialogDescription>
            {mode === 'reinstate'
              ? t('This technician will be offered interventions again.')
              : t('This technician will no longer be offered new interventions. Their ongoing missions stay, and they can still sign in.')}
          </DialogDescription>
        </DialogHeader>

        {mode !== 'reinstate' && (
          <div className="space-y-1.5">
            <Label htmlFor={REASON_ID}>
              {t('Reason')}
              <span className="text-rose-500"> *</span>
            </Label>
            <Select
              value={reason ?? ''}
              onValueChange={(next) => next && setReason(next as ExclusionReason)}
            >
              <SelectTrigger
                id={REASON_ID}
                aria-invalid={missingReason || undefined}
                aria-describedby={missingReason ? `${REASON_ID}-error` : undefined}
              >
                <SelectValue placeholder={t('Choose a reason')} />
              </SelectTrigger>
              <SelectContent>
                {EXCLUSION_REASONS.map((code) => (
                  <SelectItem key={code} value={code}>
                    {exclusionReasonLabel(t, code)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {missingReason ? (
              <p id={`${REASON_ID}-error`} className="text-xs text-rose-600 dark:text-rose-400">
                {t('Choose a reason')}
              </p>
            ) : (
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {t('Closed list: the reason feeds the filters and the export.')}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" aria-disabled={pending} onClick={() => !pending && onClose()}>
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            variant={mode === 'exclude' ? 'destructive' : 'default'}
            aria-disabled={pending || unchanged}
            onClick={confirm}
          >
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExclusionDialog;
